/**
 * Client for Ava, the Bloom assistant (/api/agent). Uses fetch rather than the
 * shared axios client because chat responses stream as Server-Sent Events and
 * goal classification can outlast axios's 10s timeout.
 */

const baseURL = import.meta.env.VITE_API_URL || '/api';


export interface AgentGoal {
  id: string;
  label: string;
  description: string;
  starterPrompts: string[];
}

export type InterviewStatus = 'not_started' | 'in_progress' | 'complete';

export interface AgentMemory {
  goal: { id: string; label: string; rationale: string | null } | null;
  interviewStatus: InterviewStatus;
  topics: { id: string; label: string; answer: string | null; updatedAt: string | null }[];
  notes: { id: string; text: string; createdAt: string }[];
}

export interface AgentStatus {
  available: boolean;
  assistant: { name: string; tagline: string };
  user: { firstName: string; role: string };
  goals: AgentGoal[];
  kickoffMessage: string;
  memory: AgentMemory;
}

export interface AgentConversationSummary {
  id: string;
  title?: string;
  goal?: string;
  updatedAt: string;
  totalCostUsd: number;
}

export type AgentActionStatus = 'pending' | 'confirmed' | 'rejected' | 'failed' | 'expired';

export interface AgentAction {
  id: string;
  tool: string;
  summary: string;
  status: AgentActionStatus;
  result?: string;
  expiresAt: string;
}

export interface AgentConversationDetail {
  id: string;
  title?: string;
  goal?: string;
  messages: { role: 'user' | 'assistant'; text: string }[];
  actions: AgentAction[];
  totalCostUsd: number;
}

export type AgentStreamEvent =
  | { type: 'conversation'; conversationId: string }
  | { type: 'text'; text: string }
  | { type: 'tool'; name: string }
  | { type: 'pending_action'; action: { id: string; tool: string; summary: string; expiresAt: string } }
  | { type: 'memory'; event: { kind: 'saved' | 'noted' | 'forgotten' | 'goal' | 'interview_complete'; label: string } }
  | { type: 'done'; conversationId: string; stopReason: string | null; usage: { costUsd: number } }
  | { type: 'error'; message: string };

/**
 * Content-Type is only sent with a body: Fastify rejects a request that
 * declares JSON but sends nothing (e.g. the bodiless confirm/delete calls).
 */
function authHeaders(withBody: boolean): Record<string, string> {
  const token = localStorage.getItem('bloom_token');
  return {
    ...(withBody ? { 'Content-Type': 'application/json' } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };
}

async function request<T>(method: 'GET' | 'POST' | 'PUT' | 'DELETE', path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${baseURL}${path}`, {
    method,
    headers: authHeaders(body !== undefined),
    body: body === undefined ? undefined : JSON.stringify(body)
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.success === false) {
    throw new Error(json.error || (res.status === 401 ? 'Your session expired. Reload the page to sign in again.' : `Request failed (${res.status})`));
  }
  return json.data as T;
}

export const agentApi = {
  status: () => request<AgentStatus>('GET', '/agent/status'),
  setGoal: (body: { goalId?: string; statedGoal?: string }) =>
    request<{ goal: string; source: string; rationale?: string }>('POST', '/agent/goal', body),
  conversations: () => request<AgentConversationSummary[]>('GET', '/agent/conversations'),
  conversation: (id: string) => request<AgentConversationDetail>('GET', `/agent/conversations/${id}`),
  memory: () => request<AgentMemory>('GET', '/agent/memory'),
  updateProfileAnswer: (topic: string, answer: string) => request<AgentMemory>('PUT', `/agent/memory/profile/${encodeURIComponent(topic)}`, { answer }),
  forgetProfileAnswer: (topic: string) => request<AgentMemory>('DELETE', `/agent/memory/profile/${encodeURIComponent(topic)}`),
  forgetNote: (id: string) => request<AgentMemory>('DELETE', `/agent/memory/notes/${encodeURIComponent(id)}`),
  forgetEverything: () => request<AgentMemory>('DELETE', '/agent/memory'),
  confirmAction: (id: string) => request<{ status: AgentActionStatus; result: string }>('POST', `/agent/actions/${id}/confirm`),
  rejectAction: (id: string) => request<{ status: AgentActionStatus }>('POST', `/agent/actions/${id}/reject`),

  /** Send a message and receive the reply as a stream of events. */
  async chat(
    body: { conversationId?: string; message?: string; kickoff?: 'interview' },
    onEvent: (event: AgentStreamEvent) => void,
    signal?: AbortSignal
  ): Promise<void> {
    const res = await fetch(`${baseURL}/agent/chat`, {
      method: 'POST',
      headers: authHeaders(true),
      body: JSON.stringify(body),
      signal
    });
    if (!res.ok || !res.body) {
      const json = await res.json().catch(() => ({}));
      throw new Error(json.error || `Request failed (${res.status})`);
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let boundary: number;
      while ((boundary = buffer.indexOf('\n\n')) !== -1) {
        const chunk = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        const data = chunk.split('\n').filter(l => l.startsWith('data: ')).map(l => l.slice(6)).join('\n');
        if (data) onEvent(JSON.parse(data));
      }
    }
  }
};
